interface Props {
  /** OTAV → Collecteur: the collector is receiving data from the active OTAV. */
  otavConnected: boolean;
  /**
   * The active OTAV's REST API answers. Live detection runs on it — the ON AIR
   * badge, the next-live duration, the following live. It can die while the
   * WebSocket (and therefore `otavConnected`) stays perfectly healthy.
   */
  otavRestOk: boolean;
  /** Collecteur → Cloud: the cloud server has an active collector bridge. */
  collectorConnected: boolean;
  /** Cloud → Client: this browser's WebSocket to the cloud is open. */
  clientConnected: boolean;
  /** NTP server for this channel has a successful sync (no recent error). */
  ntpSynced: boolean;
}

/**
 * Compact service-health indicator. A master pastille (green if all five
 * checks pass, red otherwise) with a hover-revealed panel showing the
 * per-link status. Kept intentionally terse — this dashboard is client-
 * facing, not an ops console.
 */
export function StatusIndicator({
  otavConnected,
  otavRestOk,
  collectorConnected,
  clientConnected,
  ntpSynced,
}: Props) {
  const allGreen =
    otavConnected && otavRestOk && collectorConnected && clientConnected && ntpSynced;

  return (
    <div className="group relative flex items-center gap-1.5 text-[10px] uppercase tracking-widest">
      <span className={`h-1.5 w-1.5 rounded-full ${allGreen ? 'bg-emerald-500' : 'bg-red-500'}`} />
      <span className={allGreen ? 'text-neutral-500' : 'text-red-500'}>Statuts</span>

      {/* Hover panel — absolutely positioned so it overlays the footer.
          Anchored to the LEFT edge, not centred: this indicator sits against
          the left edge of the page, so a centred panel overflowed off-screen
          on anything narrower than ~1 690 px — hiding the status dots, which
          are the whole point of the panel. Measured on 2026-09-23 at 1 024 px:
          panel at −26 px, all five dots at x = −9, none visible. */}
      <div
        className="pointer-events-none absolute bottom-full left-0 z-10 mb-2 rounded-lg border border-neutral-800 bg-neutral-900/95 px-4 py-3 opacity-0 shadow-lg backdrop-blur transition-opacity duration-150 group-hover:pointer-events-auto group-hover:opacity-100"
        role="tooltip"
      >
        <ul className="flex flex-col gap-2 whitespace-nowrap text-neutral-300">
          <StatusRow label="OTAV → Collecteur" ok={otavConnected} />
          <StatusRow label="Détection des directs" ok={otavRestOk} />
          <StatusRow label="Collecteur → Cloud" ok={collectorConnected} />
          <StatusRow label="Cloud → Client" ok={clientConnected} />
          <StatusRow label="Synchro NTP" ok={ntpSynced} />
        </ul>
      </div>
    </div>
  );
}

function StatusRow({ label, ok }: { label: string; ok: boolean }) {
  return (
    <li className="flex items-center gap-2">
      <span className={`h-1.5 w-1.5 rounded-full ${ok ? 'bg-emerald-500' : 'bg-red-500'}`} />
      <span>{label}</span>
    </li>
  );
}
